import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, RefreshCcw, GitCompare, Search, Filter, ChevronRight, ArrowRight, TrendingUp, X } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { Ticker } from "@/components/TickerDrawer";

const VERDICT_TONE = {
  A: "text-signal-green", B: "text-signal-blue", C: "text-signal-yellow",
  BUY: "text-signal-green", STRONG_BUY: "text-signal-green",
  WATCH: "text-signal-blue", AVOID: "text-signal-red",
  BUY_ON_PULLBACK: "text-signal-blue",
};
const FRESH_TONE = {
  fresh: "bg-signal-green/15 text-signal-green border-signal-green/40",
  aging: "bg-signal-yellow/15 text-signal-yellow border-signal-yellow/40",
  stale: "bg-signal-red/10 text-signal-red border-signal-red/30",
  expired: "bg-signal-red/20 text-signal-red border-signal-red/60",
  unknown: "text-muted-foreground border-border/60",
};

function humanAge(sec) {
  if (sec === null || sec === undefined) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}

function VersionsDrawer({ open, onClose, symbol, rootId, onCompare }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    if (!open || !rootId) return;
    api.get(`/analysis/${rootId}/versions`).then(r => setItems(r.data));
    setSelected([]);
  }, [open, rootId]);
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : (s.length >= 2 ? [s[1], id] : [...s, id]));
  if (!rootId) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="!rounded-none border-border/70 max-w-3xl" data-testid="versions-drawer">
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-wider text-sm">
            {symbol} · Analysis Versions
          </DialogTitle>
        </DialogHeader>
        <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">
          Select any 2 versions to compare.
        </div>
        <div className="max-h-96 overflow-auto border border-border/40">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
              <tr>
                <th className="w-6"></th>
                <th className="text-left px-2 py-1.5 font-normal">Ver</th>
                <th className="text-left px-2 py-1.5 font-normal">Completed</th>
                <th className="text-left px-2 py-1.5 font-normal">Verdict</th>
                <th className="text-right px-2 py-1.5 font-normal">Price</th>
                <th className="text-right px-2 py-1.5 font-normal">Score</th>
                <th className="text-right px-2 py-1.5 font-normal">Confidence</th>
                <th className="text-right px-2 py-1.5 font-normal">Fresh</th>
              </tr>
            </thead>
            <tbody>
              {items.map(v => {
                const m = v.analysis_meta || {};
                const isSel = selected.includes(v.id);
                return (
                  <tr key={v.id} onClick={() => toggle(v.id)}
                    className={`border-t border-border/30 cursor-pointer hover:bg-secondary/25 ${isSel ? "bg-secondary/40" : ""}`}
                    data-testid={`version-row-${v.version}`}>
                    <td className="px-2 text-center"><input type="checkbox" checked={isSel} onChange={() => {}} className="pointer-events-none" /></td>
                    <td className="px-2 py-1.5 mono">v{v.version}</td>
                    <td className="px-2 py-1.5 mono text-[10px] text-muted-foreground">{new Date(v.completed_at).toLocaleString()}</td>
                    <td className={`px-2 py-1.5 mono ${VERDICT_TONE[m.verdict] || ""}`}>{m.verdict || "—"}</td>
                    <td className="px-2 py-1.5 mono text-right">${(m.price_at_analysis || 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 mono text-right">{m.opportunity_score ?? "—"}</td>
                    <td className="px-2 py-1.5 mono text-right">{m.confidence ?? "—"}</td>
                    <td className="px-2 py-1.5 mono text-right text-[10px]">{v.freshness?.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <button className="btn-outline" onClick={onClose}>Close</button>
          <button className="btn-primary" disabled={selected.length !== 2}
            onClick={() => onCompare(selected[0], selected[1])}
            data-testid="compare-btn">
            <GitCompare size={13} /> Compare Selected
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompareDialog({ open, onClose, a, b }) {
  const [diff, setDiff] = useState(null);
  useEffect(() => {
    if (!open || !a || !b) return;
    api.get("/analysis/compare/pair", { params: { a, b } }).then(r => setDiff(r.data));
  }, [open, a, b]);
  if (!open) return null;
  const rows = diff ? Object.entries(diff.fields) : [];
  const deltaTone = (d) => ({
    increased: "pnl-up", decreased: "pnl-down",
    added: "pnl-up", removed: "pnl-down",
    changed: "text-signal-yellow", unchanged: "text-muted-foreground",
  }[d] || "");
  const deltaGlyph = (d) => ({ increased: "▲", decreased: "▼", added: "+", removed: "−", changed: "≠", unchanged: "=" }[d] || "");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="!rounded-none border-border/70 max-w-4xl" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-wider text-sm">
            Version Comparison
          </DialogTitle>
        </DialogHeader>
        {!diff && <div className="text-center p-6"><Loader2 className="inline animate-spin" size={14} /> Loading…</div>}
        {diff && (
          <div className="border border-border/40 max-h-[70vh] overflow-auto">
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-px bg-border/60 sticky top-0 bg-card">
              <div className="p-2 bg-card"><div className="label-cap">Field</div></div>
              <div className="p-2 bg-card"><div className="label-cap">v{diff.a_version} · {new Date(diff.a_completed).toLocaleString()}</div></div>
              <div className="p-2 bg-card"><div className="label-cap">v{diff.b_version} · {new Date(diff.b_completed).toLocaleString()}</div></div>
            </div>
            {rows.map(([label, row]) => {
              const isList = Array.isArray(row.a) || Array.isArray(row.b);
              return (
                <div key={label} className="grid grid-cols-[1fr_1fr_1fr] gap-px bg-border/60">
                  <div className="p-2 bg-card/40 text-xs">
                    <div className="mono uppercase tracking-widest text-[10px] text-muted-foreground">{label.replace(/_/g," ")}</div>
                    <div className={`mono text-[11px] mt-1 ${deltaTone(row.delta)}`}>
                      {deltaGlyph(row.delta)} {row.delta}
                      {row.change !== undefined && <span className="ml-1">({row.change > 0 ? "+" : ""}{row.change})</span>}
                    </div>
                  </div>
                  <div className="p-2 bg-card/40 mono text-xs">{isList ? (row.a || []).join(", ") || "—" : (row.a ?? "—")}</div>
                  <div className="p-2 bg-card/40 mono text-xs">
                    {isList ? (
                      <>
                        {(row.a || []).map((x, i) => <span key={i} className={`inline-block mr-1 ${(row.removed || []).includes(x) ? "pnl-down line-through" : "text-muted-foreground"}`}>{x}</span>)}
                        {(row.added || []).map((x, i) => <span key={`+${i}`} className="pnl-up inline-block mr-1">+{x}</span>)}
                      </>
                    ) : (row.b ?? "—")}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <button className="btn-outline" onClick={onClose}>Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AnalysisHistory() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ symbol: "", verdict: "", category: "", sort_by: "newest" });
  const [versionsFor, setVersionsFor] = useState(null); // { rootId, symbol }
  const [comparePair, setComparePair] = useState(null); // { a, b }
  const [refreshing, setRefreshing] = useState({});
  const nav = useNavigate();
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 30, ...filters };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const { data } = await api.get("/analysis/history", { params });
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page, filters]); // eslint-disable-line

  const doRefresh = async (row) => {
    const m = row.analysis_meta || {};
    const ok = await confirm.confirm({
      variant: "budget",
      title: `Refresh ${row.symbol} Analysis?`,
      description: "This will retrieve fresh market data and rerun the Trade Analyst, Risk Critic, and Risk Engine. A new immutable version will be created.",
      details: {
        symbol: row.symbol,
        "current verdict": m.verdict || "—",
        "current age": humanAge(row.freshness?.age_seconds),
        "estimated AI cost": "~$0.045",
      },
      confirmLabel: "Refresh Full Analysis",
      testId: "refresh-analysis-confirm",
    });
    if (!ok) return;
    setRefreshing(r => ({ ...r, [row.id]: true }));
    try {
      const { data } = await api.post("/analysis/refresh", { symbol: row.symbol, parent_analysis_id: row.id });
      if (data.duplicate) {
        toast.info(`Refresh for ${row.symbol} already in progress.`);
      } else {
        toast.success(`Refresh queued for ${row.symbol} · job ${data.job.id.slice(0, 8)}`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Refresh failed");
    } finally { setRefreshing(r => ({ ...r, [row.id]: false })); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="analysis-history-root">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-medium tracking-tight flex items-center gap-2"><TrendingUp size={18} /> Analysis History</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Every completed analysis is saved automatically. Reopen a symbol without re-running the AI.</p>
        </div>
        <div className="ml-auto mono text-xs text-muted-foreground">{total} analyses</div>
      </div>

      <div className="grid-cell !p-3 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input placeholder="Symbol" value={filters.symbol} className="!rounded-none !pl-7 mono h-8"
            onChange={(e) => { setFilters({ ...filters, symbol: e.target.value }); setPage(1); }}
            data-testid="filter-symbol"/>
        </div>
        <select className="bg-transparent border border-border/60 h-8 px-2 text-xs mono" value={filters.verdict}
          onChange={(e) => { setFilters({ ...filters, verdict: e.target.value }); setPage(1); }} data-testid="filter-verdict">
          <option value="">Any verdict</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
        <select className="bg-transparent border border-border/60 h-8 px-2 text-xs mono" value={filters.category}
          onChange={(e) => { setFilters({ ...filters, category: e.target.value }); setPage(1); }} data-testid="filter-category">
          <option value="">Any category</option>
          <option value="TACTICAL">Tactical</option>
          <option value="SWING">Swing</option>
          <option value="LONG_TERM">Long-Term</option>
        </select>
        <select className="bg-transparent border border-border/60 h-8 px-2 text-xs mono" value={filters.sort_by}
          onChange={(e) => { setFilters({ ...filters, sort_by: e.target.value }); setPage(1); }} data-testid="filter-sort">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="score">Highest score</option>
          <option value="confidence">Highest confidence</option>
          <option value="risk_low">Lowest risk</option>
        </select>
        <button className="btn-outline h-8" onClick={() => { setFilters({ symbol: "", verdict: "", category: "", sort_by: "newest" }); setPage(1); }} data-testid="filter-reset">
          <X size={12} /> Reset
        </button>
      </div>

      <div className="border border-border/60">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
            <tr>
              <th className="text-left px-3 py-1.5 font-normal">Symbol</th>
              <th className="text-left px-3 py-1.5 font-normal">Category</th>
              <th className="text-left px-3 py-1.5 font-normal">Verdict</th>
              <th className="text-right px-3 py-1.5 font-normal">Score</th>
              <th className="text-right px-3 py-1.5 font-normal">Conf</th>
              <th className="text-right px-3 py-1.5 font-normal">Risk</th>
              <th className="text-right px-3 py-1.5 font-normal">Price</th>
              <th className="text-center px-3 py-1.5 font-normal">Fresh</th>
              <th className="text-right px-3 py-1.5 font-normal">Age</th>
              <th className="text-center px-3 py-1.5 font-normal">Versions</th>
              <th className="text-right px-3 py-1.5 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={14}/> Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">No analyses yet. Run one from the Dashboard.</td></tr>}
            {rows.map(r => {
              const m = r.analysis_meta || {};
              return (
                <tr key={r.id} className="border-t border-border/30 hover:bg-secondary/25">
                  <td className="px-3 py-2 mono">
                    <Ticker symbol={r.symbol}/>
                    {r.freshness?.age_seconds !== null && r.freshness?.age_seconds !== undefined && r.freshness.age_seconds < 900 && (
                      <span className="ml-2 chip !text-[9px] pnl-up border-current" data-testid={`refreshed-indicator-${r.symbol}`} title={`Refreshed ${humanAge(r.freshness.age_seconds)} ago`}>
                        ● Just refreshed
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 mono text-[10px] uppercase text-muted-foreground">{m.category || "—"}</td>
                  <td className={`px-3 py-2 mono ${VERDICT_TONE[m.verdict] || ""}`}>{m.verdict || "—"}</td>
                  <td className="px-3 py-2 mono text-right">{m.opportunity_score ?? "—"}</td>
                  <td className="px-3 py-2 mono text-right">{m.confidence ?? "—"}</td>
                  <td className="px-3 py-2 mono text-right">{m.risk_score ?? "—"}</td>
                  <td className="px-3 py-2 mono text-right">${(m.price_at_analysis || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-center"><span className={`chip !text-[10px] ${FRESH_TONE[r.freshness?.status] || FRESH_TONE.unknown}`}>{r.freshness?.status || "?"}</span></td>
                  <td className="px-3 py-2 mono text-right text-muted-foreground">{humanAge(r.freshness?.age_seconds)}</td>
                  <td className="px-3 py-2 text-center mono">{r.version_count || 1}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={() => nav(`/proposal/${r.id}`)} data-testid={`open-${r.symbol}`}>Open <ArrowRight size={10}/></button>
                      <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={() => setVersionsFor({ rootId: r.root_id || r.id, symbol: r.symbol })} data-testid={`versions-${r.symbol}`}>v{r.version_count || 1}</button>
                      <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={() => doRefresh(r)} disabled={refreshing[r.id]} data-testid={`refresh-${r.symbol}`}>
                        {refreshing[r.id] ? <Loader2 className="animate-spin" size={10}/> : <RefreshCcw size={10}/>}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <VersionsDrawer
        open={!!versionsFor} onClose={() => setVersionsFor(null)}
        symbol={versionsFor?.symbol} rootId={versionsFor?.rootId}
        onCompare={(a, b) => { setVersionsFor(null); setComparePair({ a, b }); }}
      />
      <CompareDialog open={!!comparePair} onClose={() => setComparePair(null)} a={comparePair?.a} b={comparePair?.b} />
    </div>
  );
}
